import React, { useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';

import {
  useCreateOpportunityProject,
  useGetOpportunityProject,
  useUpdateProjectTask,
} from '~/hooks/apis/contracting/useOpportunityProject';
import { useGetOffer, useGetOpportunity } from '~/hooks/apis/offers/useGetOffer';

import type {
  NewProjectValues,
  ProjectAssignee,
  ProjectTaskChanges,
  ProjectTaskStatus,
} from '~/services/ContractingService';

import TabStrip from '../shared/TabStrip';

import CreateProjectDialog from './CreateProjectDialog';
import StageHistoryTable from './StageHistoryTable';
import TaskBoard from './TaskBoard';
import TaskListTable from './TaskListTable';
import { formatProjectDate } from './projectManagementModel';

type ProjectTab = 'board' | 'list' | 'stage';

const TABS: ReadonlyArray<{ label: string; value: ProjectTab }> = [
  { label: 'Board view', value: 'board' },
  { label: 'List view', value: 'list' },
  { label: 'Stage history', value: 'stage' },
];

export default function OfferBuilderProjectManagementPage() {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const { data: offer, error: offerError } = useGetOffer(offerId);
  const salesforceOpportunityId = offer?.id_salesforce_external;
  const { data: opportunity, error: opportunityError } = useGetOpportunity(salesforceOpportunityId, {
    enabled: !!salesforceOpportunityId,
  });
  const { enqueueSnackbar } = useSnackbar();
  const { data: project, error } = useGetOpportunityProject(salesforceOpportunityId);
  const createProject = useCreateOpportunityProject(salesforceOpportunityId ?? '');
  const updateTask = useUpdateProjectTask(salesforceOpportunityId ?? '');
  const [activeTab, setActiveTab] = useState<ProjectTab>('board');
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const handleCreateProject = async (values: NewProjectValues) => {
    await createProject.mutateAsync({ opportunityName: opportunity?.opportunity_info.name ?? '', values });
    setActiveTab('board');
    setIsCreateOpen(false);
  };

  const handleUpdateTask = (taskId: string, changes: ProjectTaskChanges) => {
    if (!project) {
      return;
    }
    updateTask.mutate(
      { projectId: project.id, taskId, changes },
      { onError: (taskError) => enqueueSnackbar(`Task not saved: ${taskError.message}`, { variant: 'error' }) },
    );
  };

  const handleMoveTask = (taskId: string, status: ProjectTaskStatus, beforeTaskId?: string) => {
    handleUpdateTask(taskId, { status, beforeTaskId });
  };

  const handleChangeAssignees = (taskId: string, assignees: Array<ProjectAssignee>) => {
    handleUpdateTask(taskId, { assignees });
  };

  const hasNoOpportunity = !!offer && !salesforceOpportunityId;
  // `project` stays undefined until the offer and project have both loaded; null means none exists.
  const isLoadingProject = !offerError && !error && !hasNoOpportunity && project === undefined;
  const emptyMessage = hasNoOpportunity
    ? 'Link this offer to a Salesforce opportunity to create a project.'
    : 'No project has been created for this opportunity yet. Create one from a template to generate its task board.';

  return (
    <>
      <Helmet>
        <title>Project Management | Offer Builder</title>
      </Helmet>

      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4">Project Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Production project and task board for this opportunity.
          </Typography>
        </Box>
        <Button
          disabled={!opportunity}
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => setIsCreateOpen(true)}
        >
          New project
        </Button>
      </Stack>

      {offerError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load the offer: {offerError.message}
        </Alert>
      )}
      {opportunityError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load the Salesforce opportunity: {opportunityError.message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load the project: {error.message}
        </Alert>
      )}

      {isLoadingProject && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {project && (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6">{project.name}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {project.templateName} · {project.estimatedHours} estimated hours · {formatProjectDate(project.startDate)}{' '}
              – {formatProjectDate(project.endDate)}
            </Typography>
          </Box>

          <TabStrip<ProjectTab> activeTab={activeTab} counts={{}} tabs={TABS} onTabChange={setActiveTab} />

          <Box sx={{ mt: 2 }}>
            {activeTab === 'board' && (
              <TaskBoard tasks={project.tasks} onChangeAssignees={handleChangeAssignees} onMoveTask={handleMoveTask} />
            )}
            {activeTab === 'list' && <TaskListTable project={project} />}
            {activeTab === 'stage' && <StageHistoryTable />}
          </Box>
        </>
      )}

      {(project === null || hasNoOpportunity) && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <ViewKanbanIcon color="disabled" fontSize="large" />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {emptyMessage}
          </Typography>
        </Paper>
      )}

      <CreateProjectDialog
        currentProjectName={project?.name}
        isSaving={createProject.isPending}
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreate={handleCreateProject}
      />
    </>
  );
}
